import "features/ad-hoc-audiences/audiences.page.scss";
import { AudiencesTable } from "./audiences-table.tsx";
import React, { useMemo, useState } from "react";
import { useFuzzySearch } from "../../core/hooks/useFuzzySearch.ts";
import { AudienceSearch } from "./audiences-search.tsx";
import { AudiencesError } from "./audiences-error.tsx";
import { IAudience } from "@lib/types";
import { FuseResult } from "fuse.js";
import { useParams } from "react-router-dom";
import { useStateContext } from "state";
import { AudiencesZeroState } from "./audiences-zero-state.tsx";
import { useProject, useUpdateProject } from "hooks/projects";
import { EditableDescription } from "features/ad-hoc-projects/editable-description.tsx";
import { useTransactionReportDownload } from "../../hooks/transaction/useTransactionReportDownload.tsx";
import { useReconciliationReportDownload } from "../../hooks/reconciliation/useReconciliationReportDownload.tsx";
import { AxButton } from "@dynata/axiom3";
import { useFeatureFlag } from "core/hooks/useFeatureFlag";

export function AudiencesPage() {
  const { user } = useStateContext();
  const { projectId, id } = useParams();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isTransactionsLoading, setIsTransactionsLoading] = useState(false);
  const [isReconciliationsLoading, setIsReconciliationsLoading] =
    useState(false);
  const canDownloadReports = useFeatureFlag("ReportDownload");

  const { mutateAsync: generateTransactionReport } =
    useTransactionReportDownload();
  const { mutateAsync: generateReconciliationReport } =
    useReconciliationReportDownload();

  const accountNumber = useMemo(
    () =>
      user?.isSingleAdHocAccount
        ? parseInt(user.roles[0].accountNumber)
        : parseInt(id!),
    [user, id],
  );

  const {
    data: project,
    isFetching: isProjectFetching,
    isError: isProjectError,
  } = useProject(accountNumber, parseInt(projectId!), {
    params: { expand: "audiences" },
  });
  const { mutateAsync: updateProject } = useUpdateProject(
    accountNumber,
    parseInt(projectId!),
  );

  const submitProjectUpdate = async (value: string) => {
    return await updateProject({ description: value });
  };

  // Get the full list of audiences from the project
  const audiences: IAudience[] = useMemo(() => {
    return project?.audiences ?? [];
  }, [project]);

  // Filter the list of audiences based on search term
  const searchResults: FuseResult<IAudience>[] = useFuzzySearch(
    audiences,
    searchTerm,
    {
      keys: ["context_id"],
      ignoreLocation: true,
      threshold: 0,
    },
  );

  // Determine the data to display in the table
  const tableData: IAudience[] = useMemo(() => {
    return searchTerm ? searchResults.map((result) => result.item) : audiences;
  }, [audiences, searchTerm, searchResults]);

  // render flags
  const shouldRenderZeroState: boolean =
    !isProjectFetching && audiences.length === 0;
  const shouldRenderAudiencesTable: boolean =
    !isProjectError && !shouldRenderZeroState;

  // If there was an error fetching the project, show the error component
  if (isProjectError) {
    return <AudiencesError />;
  }

  // If there are no audiences, show the zero state component
  if (shouldRenderZeroState) {
    return <AudiencesZeroState />;
  }

  const date = new Date();
  const month = date.getMonth() + 1;

  const downloadReconciliations = async () => {
    const request = {
      account: accountNumber.toString(),
      year: date.getFullYear(),
      project_id: parseInt(projectId!),
      report_type: "reconciliations",
      month,
    };
    setIsReconciliationsLoading(true);
    await generateReconciliationReport(request);
    setIsReconciliationsLoading(false);
  };

  const downloadTransactions = async () => {
    const request = {
      account: accountNumber.toString(),
      year: date.getFullYear(),
      project_id: parseInt(projectId!),
      report_type: "all",
      month,
    };

    setIsTransactionsLoading(true);
    await generateTransactionReport(request);
    setIsTransactionsLoading(false);
  };

  return (
    <div className="audiences-page">
      {project ? (
        <EditableDescription
          initialValue={project.description}
          onSubmit={submitProjectUpdate}
        >
          {project.description}
        </EditableDescription>
      ) : null}
      <div className="audiences-header">
        <h2>Audiences</h2>
        {canDownloadReports && (
          <div className="audiences-header-downloads">
            <div className="reconciliations-download">
              <AxButton
                loading={isReconciliationsLoading}
                onClick={downloadReconciliations}
                color="positive"
                type="primary"
              >
                Download Reconciliations
              </AxButton>
            </div>
            <div className="transactions-download">
              <AxButton
                loading={isTransactionsLoading}
                onClick={downloadTransactions}
                color="positive"
                type="primary"
              >
                Download Transactions
              </AxButton>
            </div>
          </div>
        )}
      </div>
      {/*Search component*/}
      <AudienceSearch
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />
      {/*Audiences table*/}
      {shouldRenderAudiencesTable && (
        <>
          <AudiencesTable
            data={tableData}
            loading={isProjectFetching}
            accountNumber={accountNumber}
            projectId={projectId!}
          />
        </>
      )}
    </div>
  );
}
